import { useCdiNudgeState } from '@/container/session-page/cdi-nudges/useCdiNudgeState';
import { useIsWorkInSessionEnabled } from '@/features/flags';
import type { SessionResearchVerb } from '@heidi/agents';
import { useWorkPanelProjectId } from '@heidi/agents/data';
import { workCanvasSurfaceClassName } from '@heidi/utils';
import dynamic from 'next/dynamic';
import { Fragment, type ReactNode } from 'react';
import { useIsEvidenceChatOpen, useWorkComposerMode } from './use-persisted-work-mode';

const SessionWorkPanel = dynamic(
  () => import('@heidi/agents').then((module) => module.SessionWorkPanel),
  { ssr: false }
);

const SessionWorkPanelHeader = dynamic(
  () => import('@heidi/agents').then((module) => module.SessionWorkPanelHeader),
  { ssr: false }
);

/** What the pane decides about the Evidence chat; the shell owns the rest. */
export interface EvidenceChatRenderProps {
  chatId: string | null;
  hideHeader?: boolean;
  surfaceClassName?: string;
}

interface SessionAskHeidiPaneProps {
  sessionId: string;
  /**
   * From `useSessionAskHeidiChatId`: the session's own selection, which is the
   * only Evidence chat the pane shows. `activeSidebarChatIdAtom` is global and
   * nothing clears it on navigation, so it could carry one session's chat into
   * another.
   *
   * XXX: its only non-null writer is the transcript-chip query, whose bar
   * (`AskAIV3`) no longer mounts. Reviving it means selecting the chip chat in
   * `sidebarChatIdAtomFamily` too, and clearing that wherever the global is
   * cleared for a fresh chat.
   */
  sidebarChatId: string | null;
  /**
   * Renders the Evidence session chat (`ChatRenderer`). A render prop because
   * Chat is quarantined: the shells already import it, this pane may not.
   */
  renderEvidenceChat: (props: EvidenceChatRenderProps) => ReactNode;
  onClose?: () => void;
  /** Forwarded to `SessionWorkPanel`: an artifact open that lands on the session view. */
  onOpenInSessionView?: () => void;
  /** Flag off: still show the Work run a routine-run deep link (Next steps) selected. */
  showRoutineRunWithoutWork?: boolean;
  /**
   * Direction C verbs for the Work landing's Research card, resolved by the
   * shell (they come from Chat, which this pane may not import).
   */
  researchVerbs?: readonly SessionResearchVerb[];
  onOpenResearchVerb?: (verbId: string) => void;
}

/**
 * The in-session Ask Heidi panel body: the Work panel or the Evidence chat.
 *
 * Flag off, it is the Evidence chat exactly as before. Flag on, Work home
 * hosts every new chat; Evidence only takes the panel once an Evidence chat
 * is open (selected, or a send still waiting for its id), under the Work
 * header on the Work surface.
 */
export const SessionAskHeidiPane = ({
  sessionId,
  sidebarChatId,
  renderEvidenceChat,
  onClose,
  onOpenInSessionView,
  showRoutineRunWithoutWork = false,
  researchVerbs,
  onOpenResearchVerb,
}: SessionAskHeidiPaneProps) => {
  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();
  const isEvidenceOnly = useWorkComposerMode() === 'evidence';
  const isEvidenceChatOpen = useIsEvidenceChatOpen(sessionId, sidebarChatId);
  const workPanelProjectId = useWorkPanelProjectId(sessionId);
  // CDI nudges demo (RCM-1151): the nudges Routine's chats answer in place.
  const { nudgeProjectIds } = useCdiNudgeState(sessionId);

  const showWorkPanel = isWorkInSessionEnabled
    ? !(isEvidenceOnly && (Boolean(sidebarChatId) || isEvidenceChatOpen))
    : showRoutineRunWithoutWork && Boolean(workPanelProjectId);

  if (showWorkPanel) {
    return (
      <SessionWorkPanel
        key={sessionId}
        sessionId={sessionId}
        onClose={onClose}
        onOpenInSessionView={onOpenInSessionView}
        inScopeProjectIds={nudgeProjectIds}
        researchVerbs={researchVerbs}
        onOpenResearchVerb={onOpenResearchVerb}
      />
    );
  }

  const chat = (
    <Fragment key={sidebarChatId ?? 'new-chat'}>
      {renderEvidenceChat({
        chatId: sidebarChatId,
        ...(isWorkInSessionEnabled
          ? { hideHeader: true, surfaceClassName: workCanvasSurfaceClassName() }
          : {}),
      })}
    </Fragment>
  );

  if (!isWorkInSessionEnabled) return chat;

  return (
    <div className={workCanvasSurfaceClassName('flex size-full flex-col')}>
      <SessionWorkPanelHeader
        sessionId={sessionId}
        evidenceChatId={sidebarChatId}
        onClose={onClose}
      />
      <div className="relative min-h-0 flex-1">{chat}</div>
    </div>
  );
};
